import React, { FunctionComponent, useCallback, useEffect } from "react";
import { Formik, Field } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import Button from "../components/Button";
import { oauthEnabled } from "../../env-exports";
import InputField from "../components/InputField";

import AccountService, {
  UserCredentials,
  NotAuthorizedException,
  UserNotFoundException,
} from "../accountService/account.service";
import { saveReferralUrl, getReferralFromQuery } from "../redirect/referralStorage";

const EMPLOYEE_EMAIL_SUFFIX = "@halter.co.nz";

const loginSchema = Yup.object().shape({
  email: Yup.string().email().required("Email required"),
  password: Yup.string().when("email", {
    is: (email: string) => !email?.endsWith(EMPLOYEE_EMAIL_SUFFIX),
    then: (schema) => schema.required("Password required"),
  }),
});

type LoginFormProps = {
  onSuccess: (useSSO: boolean) => void;
};
export const LoginForm: FunctionComponent<LoginFormProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const initialValues: UserCredentials = { email: "", password: "" };

  const handleLoginSSO = useCallback(async () => {
    try {
      saveReferralUrl(getReferralFromQuery());
      await AccountService.ssoLogin();
      onSuccess(true);
    } catch (error) {
      console.log("there was an error with login via sso");
    }
  }, [onSuccess]);

  useEffect(() => {
    if (!oauthEnabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleLoginSSO();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleLoginSSO]);

  const handleLogin = async (
    credentials: UserCredentials,
    setError: (field: string, message: string) => void
  ) => {
    try {
      await AccountService.login(credentials);
      onSuccess(false);
    } catch (error) {
      if (
        error instanceof NotAuthorizedException ||
        error instanceof UserNotFoundException
      ) {
        setError("password", "Incorrect email or password");
      } else {
        setError("password", "Something went wrong, please try again");
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values, { setFieldError }) =>
        handleLogin(values, setFieldError)
      }
      validationSchema={loginSchema}
    >
      {({ handleSubmit, values }) => {
        const showSSOFlow = values.email.endsWith(EMPLOYEE_EMAIL_SUFFIX) && oauthEnabled;
        return (
          <form
            className="flex flex-col w-full items-center max-w-sm px-6 sm:w-96"
            onSubmit={handleSubmit}
          >
            <Field
              name="email"
              type="email"
              component={InputField}
              placeholderText="Email"
              labelText="Email"
              className="mb-8 w-full"
              autoFocus={true}
            />
            <Field
              name="password"
              type="password"
              component={InputField}
              placeholderText="Password"
              labelText="Password"
              className="mb-8 w-full"
            />
            <Button
              type="submit"
              text={"Sign in"}
              className={
                "bg-brand-900 hover:bg-gray-800 text-white font-bold py-3 px-12 my-3 mt-10 rounded-md w-full"
              }
            ></Button>
            {showSSOFlow && <Button
              type="button"
              onClick={() => handleLoginSSO()}
              text={"Use SSO"}
              className={
                "bg-brand-900 hover:bg-gray-800 text-white font-bold py-3 px-12 my-3 rounded-md w-full"
              }
            ></Button>}
            <div className="sm: pb-5">
              <Button
                type="button"
                text="I forgot my password"
                onClick={() => {
                  navigate("/recover-account" + window.location.search);
                }}
                className={"text-black font-bold border-b-2 border-black"}
              ></Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
